import type { KeyboardEvent } from 'react';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { EyebrowLabel, NameInput, Wrapper } from './templateNameField.styled';

interface Props {
	value: string;
	onChange: (value: string) => void;
	/** Normalisation on leaving the field; the host's Save is what persists. */
	onBlur?: () => void;
	/** Eyebrow above the input, and the input's accessible name. Defaults to the global
	 *  template copy; a deal-level master document passes its own. */
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	/** Validation message from the host's form, shown under the input. */
	error?: string;
}

/** A document's own name, editable in place at the top of an editor's side panel. */
export const TemplateNameField = ({
	value,
	onChange,
	onBlur,
	label,
	placeholder,
	disabled,
	error,
}: Props) => {
	const tDefault = useTranslationDefault();

	// Enter leaves the field, so the keyboard path and the pointer path both normalise through
	// the host's onBlur rather than by two different routes.
	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.currentTarget.blur();
		}
	};

	return (
		<Wrapper>
			<EyebrowLabel>{label ?? tDefault('document_templates.name.eyebrow')}</EyebrowLabel>
			<NameInput
				data-testid="template-name-input"
				aria-label={label ?? tDefault('document_templates.name.aria')}
				placeholder={placeholder ?? tDefault('document_templates.name.placeholder')}
				value={value}
				disabled={disabled}
				error={error}
				onChange={(event) => onChange(event.currentTarget.value)}
				onBlur={onBlur}
				onKeyDown={handleKeyDown}
			/>
		</Wrapper>
	);
};
